import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Input, Select, Space, Typography, message } from 'antd';
import { MailOutlined, SaveOutlined, SendOutlined } from '@ant-design/icons';
import type { SimplifiedPendingOrder } from '@/services/PendingOrdersService';
import OrderEmailService, { type OrderEmailRecipient } from '@/services/OrderEmailService';
import OrderEmailContent from './OrderEmailContent';

const { Text } = Typography;
const { TextArea } = Input;

interface OrderEmailPanelProps {
  order: SimplifiedPendingOrder | null;
  visible: boolean;
  onGeneratePdfBase64: () => Promise<{ base64: string; filename: string }>;
}

const OrderEmailPanel: React.FC<OrderEmailPanelProps> = ({ order, visible, onGeneratePdfBase64 }) => {
  const [loading, setLoading] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [recipients, setRecipients] = useState<OrderEmailRecipient[]>([]);
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [customMessage, setCustomMessage] = useState('');

  const sanitizeEmails = (vals: any[]) =>
    Array.from(new Set((vals || []).map((v) => String(v || '').trim()).filter((v) => v)));

  const recipientOptions = useMemo(
    () =>
      recipients.map((r) => ({
        label: `${r.name} <${r.email}>`,
        value: r.email,
      })),
    [recipients],
  );

  const loadData = useCallback(async () => {
    if (!visible) return;
    try {
      const rawType = order?.order_type || order?.document_number?.split('-')[0];
      const orderType = ['CPT', 'CPV', 'CPE'].includes(String(rawType).toUpperCase())
        ? String(rawType).toUpperCase()
        : 'CPT';

      const [loadedRecipients, prefs] = await Promise.all([
        OrderEmailService.getRecipients(),
        OrderEmailService.getPreferences(orderType),
      ]);
      setRecipients(loadedRecipients || []);
      setToEmails(prefs?.to_emails || []);
      setCcEmails(prefs?.cc_emails || []);
      setBccEmails(prefs?.bcc_emails || []);
      setCustomMessage(prefs?.custom_message || '');
    } catch (err: any) {
      const detail = err?.response?.data?.message || err?.message || 'Error cargando opciones de correo';
      message.error(detail);
    }
  }, [visible, order?.order_type]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const savePreferences = useCallback(async () => {
    setSavingPrefs(true);
    try {
      const rawType = order?.order_type || order?.document_number?.split('-')[0];
      const orderType = ['CPT', 'CPV', 'CPE'].includes(String(rawType).toUpperCase())
        ? String(rawType).toUpperCase()
        : 'CPT';

      const saved = await OrderEmailService.updatePreferences({
        order_type: orderType,
        to_emails: toEmails,
        cc_emails: ccEmails,
        bcc_emails: bccEmails,
        custom_message: customMessage.trim(),
      });
      setToEmails(saved?.to_emails || []);
      setCcEmails(saved?.cc_emails || []);
      setBccEmails(saved?.bcc_emails || []);
      setCustomMessage(saved?.custom_message || '');
      message.success(`Preferencias guardadas para ${orderType}`);
    } catch (err: any) {
      const detail = err?.response?.data?.message || err?.message || 'Error guardando preferencias';
      message.error(detail);
    } finally {
      setSavingPrefs(false);
    }
  }, [toEmails, ccEmails, bccEmails, customMessage, order?.order_type]);

  const sendEmail = useCallback(async () => {
    if (!order) return;
    if (!toEmails.length) {
      message.warning('Selecciona al menos un destinatario');
      return;
    }

    setLoading(true);
    try {
      const pdf = await onGeneratePdfBase64();
      // Ensure order contains common alternate fields so server templates
      // and logs receive expected keys even if some are missing upstream.
      const sessionRaw = typeof window !== 'undefined' ? localStorage.getItem('session') : null;
      let sessionUser: any = null;
      try {
        if (sessionRaw) {
          const s = JSON.parse(sessionRaw);
          sessionUser = s?.user || s || null;
        }
      } catch (e) {
        sessionUser = null;
      }

      const enrichedOrder = {
        ...order,
        // Identifiers
        id: order.id ?? order.orderId ?? order._id ?? order.document_id ?? order.documentId,
        document_number: order.document_number || order.orderNumber || order.order_number || order.documentNumber || order.document || orderNumber,
        orderNumber: order.orderNumber || order.document_number || order.order_number || order.documentNumber || order.document || orderNumber,

        // Client info
        client: order.client || order.client_id || order.fk_client || order.clientId,
        client_name: order.client_name || order.client_comercial_name || order.clientName || order.client || undefined,
        client_nit: order.client_nit || order.nit || order.clientNit || undefined,
        client_branch: order.client_branch || order.clientBranch || undefined,

        // Branch / contact
        branch: order.branch || undefined,
        branch_name: order.branch_name || order.branchName || undefined,
        contact: order.contact || undefined,

        // Dates
        creation_date: order.creation_date || order.created_at || order.createdAt || undefined,
        document_date: order.document_date || order.documentDate || order.creation_date || undefined,
        delivery_date: order.delivery_date || order.deliveryDate || order.delivery || undefined,
        due_date: order.due_date || order.dueDate || undefined,

        // Financials / totals
        document_subtotal_value:
          order.document_subtotal_value ?? order.documentSubtotalValue ?? order.subtotal ?? order.document_subtotal_value,
        document_discount_value: order.document_discount_value ?? order.documentDiscountValue ?? order.discount ?? 0,
        document_tax_value: order.document_tax_value ?? order.documentTaxValue ?? order.tax ?? 0,
        document_total_value:
          order.document_total_value ?? order.document_total_value ?? order.documentTotalValue ?? order.total_value ?? order.totalValue,

        total_items: order.total_items ?? order.totalItems ?? order.total_items ?? undefined,
        total_quantity: order.total_quantity ?? order.totalQuantity ?? order.total_quantity ?? undefined,
        total_value: order.total_value ?? order.totalValue ?? order.document_total_value ?? undefined,

        // Warehouse / observations
        warehouse_name: order.warehouse_name || order.warehouse || undefined,
        observations: order.observations ?? order.notes ?? order.observation ?? '',
        notes: order.notes ?? order.observations ?? order.observation ?? '',

        // Seller
        seller: order.seller || (sessionUser && (sessionUser.fk_user || sessionUser.id)) || order.seller,
        seller_name: order.seller_name || order.sellerName || (sessionUser && (sessionUser.name || sessionUser.user)) || '',

        // Status
        status: order.status || order.document_status || order.order_status || undefined,
        document_status: order.document_status || order.status || undefined,

        // Purchase order
        purchase_order: order.purchase_order || order.purchaseOrder || order.document_purchase_order || undefined,
      };

      // Debug: print enrichedOrder so we can inspect exact request payload in browser console
      try {
        // eslint-disable-next-line no-console
        console.debug('OrderEmailPanel - enrichedOrder to send:', enrichedOrder);
      } catch (e) {}

      await OrderEmailService.sendOrderEmail({
        order: enrichedOrder,
        pdfBase64: pdf.base64,
        filename: pdf.filename,
        toEmails,
        ccEmails,
        bccEmails,
        customMessage: customMessage.trim(),
      });
      message.success('Correo enviado');
    } catch (err: any) {
      const detail = err?.response?.data?.message || err?.message || 'Error enviando correo';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  }, [order, toEmails, ccEmails, bccEmails, customMessage, onGeneratePdfBase64]);

  return (
    <Card
      size="small"
      title={
        <Space>
          <MailOutlined />
          <span>Enviar PDF por correo</span>
        </Space>
      }
      style={{ marginBottom: 12, borderRadius: 10 }}
      extra={
        <Button icon={<SaveOutlined />} loading={savingPrefs} onClick={savePreferences}>
          Guardar preferencia
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <div style={{ border: '1px solid #e6eef8', borderRadius: 8, padding: 8, background: '#fbfdff' }}>
          <OrderEmailContent order={order} />
        </div>
        <div>
          <Text style={{ display: 'block', marginBottom: 6 }}>Para</Text>
          <Select
            mode="tags"
            tokenSeparators={[',', ';']}
            allowClear
            style={{ width: '100%' }}
            options={recipientOptions}
            value={toEmails}
            onChange={(vals) => setToEmails(sanitizeEmails(vals))}
            placeholder="Escribe un email y presiona Enter"
            optionFilterProp="label"
          />
        </div>

        <div>
          <Text style={{ display: 'block', marginBottom: 6 }}>CC</Text>
          <Select
            mode="tags"
            tokenSeparators={[',', ';']}
            allowClear
            style={{ width: '100%' }}
            options={recipientOptions}
            value={ccEmails}
            onChange={(vals) => setCcEmails(sanitizeEmails(vals))}
            placeholder="Escribe un email y presiona Enter"
            optionFilterProp="label"
          />
        </div>

        <div>
          <Text style={{ display: 'block', marginBottom: 6 }}>CCO</Text>
          <Select
            mode="tags"
            tokenSeparators={[',', ';']}
            allowClear
            style={{ width: '100%' }}
            options={recipientOptions}
            value={bccEmails}
            onChange={(vals) => setBccEmails(sanitizeEmails(vals))}
            placeholder="Escribe un email y presiona Enter"
            optionFilterProp="label"
          />
        </div>

        <div>
          <Text style={{ display: 'block', marginBottom: 6 }}>Mensaje</Text>
          <TextArea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            rows={4}
            maxLength={1500}
            placeholder="Mensaje opcional"
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={sendEmail}>
            Enviar PDF
          </Button>
        </div>
      </Space>
    </Card>
  );
};

export default OrderEmailPanel;

